// Choose the client account a pipeline record belongs to.
//
// WHY THIS REPLACED AUTOMATIC MATCHING. The create route used to guess: it
// looked for a Contact with the same email, then the same phone, and reused
// whatever it found. A shared phone number — a switchboard, a receptionist, one
// person running two businesses — was enough to file a record for one company
// under a completely different company's account. That is not a cosmetic error:
// the account carries the billing address and GSTIN, so the next quotation
// raised from that record prints the wrong client's details.
//
// Guessing fails in both directions, and both are expensive. Guess loosely and
// you get the wrong account; guess tightly and you get duplicate client
// profiles. A person picking from a list is right the first time, and is
// visible. So the account is chosen here, explicitly, and the record simply
// records what was chosen.
//
// "Nothing matches" is a first-class outcome rather than a silent fallback: it
// offers to create the contact, inline and prefilled with whatever was typed,
// so a half-completed pipeline record is never lost to a navigation.

import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Search, Building2, UserPlus, X, Check } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useContacts } from '@/features/contacts/hooks/useContacts';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Modal } from '@/shared/components/ui/Modal';
// The SAME form the Contacts menu uses, rendered here in a dialog. A cut-down
// version used to stand in for it, and a contact created that way had no
// GSTIN, address or state — exactly the fields the quotation raised moments
// later depends on.
import { ContactFormPage } from '@/features/contacts/pages/ContactFormPage';
import type { Contact } from '@/features/contacts/types';

/** What a chosen contact fills in on the record. */
export interface ContactSelection {
  contactId: string;
  business_name: string;
  contact_name: string;
  phone: string;
  email: string;
}

export function contactToSelection(c: Contact): ContactSelection {
  return {
    contactId: c.id,
    // The account is the company. For an individual trading under their own
    // name, `company` is blank and the person's name IS the account name.
    business_name: c.company || c.name || '',
    // Only a real person's name, never the company echoed back.
    contact_name: c.company && c.name !== c.company ? c.name : '',
    phone: c.phone || '',
    email: c.email || '',
  };
}

export function ContactSelectField({
  contactId,
  selectedLabel,
  onSelect,
  onClear,
  disabled = false,
}: {
  contactId: string;
  /** Shown when the chosen contact is not in the current search results. */
  selectedLabel?: string;
  onSelect: (selection: ContactSelection) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Debounced and lightweight: every keystroke used to fire a full-document
  // query plus three directory-wide counts, which a 67k-contact directory
  // could not keep up with.
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { data, isLoading } = useContacts({ search: debouncedSearch, compact: true, totalOnly: true, limit: 50 });
  const contacts = useMemo(() => data?.contacts ?? [], [data]);

  const chosen = useMemo(
    () => contacts.find((c) => c.id === contactId) ?? null,
    [contacts, contactId],
  );

  // ContactFormPage does the creating (or, on a cross-BDM duplicate, offers
  // the existing contact instead) and reports back; this only has to link
  // the result onto the record.
  const handleCreated = (c: { id: string; name: string; company: string; phone: string; email: string }) => {
    onSelect({
      contactId: c.id,
      business_name: c.company || c.name,
      contact_name: c.company && c.name !== c.company ? c.name : '',
      phone: c.phone,
      email: c.email,
    });
    setCreateOpen(false);
    setOpen(false);
    setSearch('');
    toast.success('Contact linked to this record.');
  };

  // ── Chosen ────────────────────────────────────────────────────────────────
  if (contactId) {
    const label = chosen ? (chosen.company || chosen.name) : (selectedLabel || 'Selected contact');
    const sub = chosen
      ? [chosen.company ? chosen.name : null, chosen.phone, chosen.email].filter(Boolean).join(' · ')
      : '';
    return (
      <div className="flex items-start gap-2.5 rounded-xl border border-primary/40 bg-primary/5 p-3">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-bold text-on-surface">{label}</p>
          {sub && <p className="truncate text-body-xs text-on-surface-variant">{sub}</p>}
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 rounded p-1 text-on-surface-variant transition hover:bg-surface hover:text-on-surface"
            aria-label="Choose a different client"
            title="Choose a different client"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  // ── Choosing ──────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
        <input
          type="text"
          value={search}
          disabled={disabled}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search existing clients by company, name, phone or email…"
          className="w-full rounded-xl border border-outline bg-surface py-2 pl-9 pr-3 text-body-sm text-on-surface placeholder:text-on-surface-variant/70 focus:border-primary focus:outline-none"
        />
      </div>

      {open && (
        <div className="max-h-56 overflow-y-auto rounded-xl border border-outline">
          {isLoading ? (
            <div className="p-4 text-center"><Spinner /></div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-start gap-2 p-3">
              <p className="text-body-xs text-on-surface-variant">
                {search
                  ? <>No client matches “{search}”. This looks like a new account.</>
                  : 'No clients yet.'}
              </p>
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-body-xs font-bold text-on-primary transition hover:opacity-90"
              >
                <UserPlus className="h-3.5 w-3.5" /> Create a new client
              </button>
            </div>
          ) : (
            <>
              {contacts.slice(0, 30).map((c, i) => (
                <button
                  key={c.id || `row-${i}`}
                  type="button"
                  disabled={!c.id}
                  onClick={() => { onSelect(contactToSelection(c)); setOpen(false); setSearch(''); }}
                  className="flex w-full items-start gap-2 border-b border-outline/60 px-3 py-2 text-left transition last:border-0 hover:bg-surface-container-low disabled:opacity-50"
                >
                  <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-on-surface-variant" />
                  <span className="min-w-0">
                    <span className="block truncate text-body-sm font-semibold text-on-surface">
                      {c.company || c.name}
                    </span>
                    <span className="block truncate text-body-xs text-on-surface-variant">
                      {[c.company ? c.name : null, c.creator_name ? `Owner: ${c.creator_name}` : null, c.phone, c.email].filter(Boolean).join(' · ') || 'No contact details'}
                    </span>
                  </span>
                </button>
              ))}
              {/* Always reachable, not only when the search comes up empty: the
                  right account may genuinely be new even when similar names
                  match. */}
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="flex w-full items-center gap-1.5 border-t border-outline bg-surface-container-low px-3 py-2 text-body-xs font-bold text-primary transition hover:bg-surface-container"
              >
                <UserPlus className="h-3.5 w-3.5" /> None of these — create a new client
              </button>
            </>
          )}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Client Account"
        size="2xl"
        // Backdrop clicks are off: this form has several tabs' worth of input
        // and losing it to a stray click would be infuriating.
        closeOnBackdrop={false}
      >
        <ContactFormPage
          embedded
          onCreated={handleCreated}
          onCancel={() => setCreateOpen(false)}
          initialValues={{ company: search, name: search }}
        />
      </Modal>
    </div>
  );
}

export default ContactSelectField;
